import { Component, OnInit } from '@angular/core';
import { StoreLocatorService } from '../../@shared/services/store-locator.service';
import { StoreBrandResponse, StoreLocationResponse } from '../../@shared/models/response.module';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';

@Component({
  selector: 'app-store-locator',
  standalone: false,
  templateUrl: './store-locator.html',
  styleUrl: './store-locator.css'
})
export class StoreLocator implements OnInit {
  brands: StoreBrandResponse[] = [];
  locations: StoreLocationResponse[] = [];
  filteredLocations: StoreLocationResponse[] = [];

  selectedBrands: number[] = [];
  searchQuery: string = '';

  loading: boolean = false;

  // Selected location for sidebar highlighting
  selectedLocation: StoreLocationResponse | null = null;

  // Map configuration
  mapMarkers: MapMarker[] = [];
  mapConfig: MapConfig = {
    height: 'calc(100vh - 250px)',
    mobileHeight: '450px',
    enableClustering: true,
    defaultCenter: { lat: 59.9139, lng: 10.7522 }, // Oslo, Norway
    defaultZoom: 6,
    directionsMode: false
  };

  constructor(private storeLocatorService: StoreLocatorService) {}

  ngOnInit() {
    this.loadBrands();
    this.loadLocations();
  }

  loadBrands() {
    this.storeLocatorService.getBrands().subscribe({
      next: (brands) => {
        this.brands = brands;
        // Select all brands by default
        this.selectedBrands = brands.map(b => b.brand_id);
      },
      error: (err) => {
        console.error('Error loading brands:', err);
      }
    });
  }

  loadLocations() {
    this.loading = true;
    this.storeLocatorService.getLocations().subscribe({
      next: (locations) => {
        this.locations = locations;
        this.filterLocations();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading locations:', err);
        this.loading = false;
      }
    });
  }

  filterLocations() {
    this.filteredLocations = this.locations.filter(loc => {
      // Filter by selected brands
      if (this.selectedBrands.length > 0 && !this.selectedBrands.includes(loc.brand_id)) {
        return false;
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const matchesSearch =
          loc.city?.toLowerCase().includes(query) ||
          loc.address?.toLowerCase().includes(query) ||
          loc.store_name?.toLowerCase().includes(query) ||
          loc.brand_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      return true;
    });

    this.updateMapMarkers();
  }

  updateMapMarkers() {
    this.mapMarkers = this.filteredLocations.map(loc => ({
      id: loc.location_id,
      lat: loc.latitude,
      lng: loc.longitude,
      title: loc.store_name,
      subtitle: loc.address || loc.city || '',
      color: this.getMarkerColor(loc.brand_color),
      data: loc
    }));
  }

  getMarkerColor(brandColor: string): 'red' | 'blue' | 'yellow' | 'purple' | 'orange' | 'pink' {
    // Map brand colors to available marker colors
    const colorLower = (brandColor || '').toLowerCase();
    if (colorLower.includes('#e53935') || colorLower.includes('red')) return 'red';
    if (colorLower.includes('#1e88e5') || colorLower.includes('blue') || colorLower.includes('#3498db')) return 'blue';
    if (colorLower.includes('#fdd835') || colorLower.includes('yellow')) return 'yellow';
    if (colorLower.includes('#8e24aa') || colorLower.includes('purple')) return 'purple';
    if (colorLower.includes('#fb8c00') || colorLower.includes('orange')) return 'orange';
    if (colorLower.includes('#d81b60') || colorLower.includes('pink')) return 'pink';

    // Default to a varied color based on brand_id
    const colors: Array<'red' | 'blue' | 'yellow' | 'purple' | 'orange' | 'pink'> = [
      'blue', 'orange', 'purple', 'pink', 'red', 'yellow'
    ];
    return colors[Math.abs(brandColor?.charCodeAt(1) || 0) % colors.length];
  }

  toggleBrand(brandId: number) {
    const index = this.selectedBrands.indexOf(brandId);
    if (index > -1) {
      this.selectedBrands.splice(index, 1);
    } else {
      this.selectedBrands.push(brandId);
    }
    this.filterLocations();
  }

  isBrandSelected(brandId: number): boolean {
    return this.selectedBrands.includes(brandId);
  }

  selectAllBrands() {
    this.selectedBrands = this.brands.map(b => b.brand_id);
    this.filterLocations();
  }

  clearBrandSelection() {
    this.selectedBrands = [];
    this.filterLocations();
  }

  onSearchChange() {
    this.filterLocations();
  }

  selectLocation(location: StoreLocationResponse) {
    this.selectedLocation = location;
    // Scroll to the location card
    const element = document.getElementById(`store-card-${location.location_id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight');
      setTimeout(() => element.classList.remove('highlight'), 2000);
    }
  }

  onMapMarkerClick(marker: MapMarker) {
    const location = this.locations.find(l => l.location_id === marker.id);
    if (location) {
      this.selectLocation(location);
    }
  }

  onInfoWindowAction(marker: MapMarker) {
    const location = marker.data as StoreLocationResponse;
    this.getDirections(location);
  }

  getDirections(location: StoreLocationResponse) {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`;
    window.open(url, '_blank');
  }

  openWebsite(url: string | null | undefined) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  callPhone(phone: string | null | undefined) {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  }

  getBrandColor(brandId: number): string {
    const brand = this.brands.find(b => b.brand_id === brandId);
    return brand?.brand_color || '#3498db';
  }
}
